import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    CircularProgress,
    Button,
    Card,
    Typography,
    FormControl,
    Select,
    MenuItem,
    Box,
    Stack,
    InputLabel,
    Alert,
    AlertTitle,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    LinearProgress,
    Fade,
    Divider,
    Chip,
    Fab,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import SendIcon from '@mui/icons-material/Send';
import {
    setHighlightedBuilding,
    setSelectedBuildings,
    loadPolymorphicForm,
    submitPolymorphicForm,
} from './onsite-visit.slice';
import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';
import OnsiteVisitMap from './components/Map';
import { Link, useParams } from 'react-router';
import Search from './components/Search';
import { useTranslation } from 'react-i18next';
import {
    Building,
    FormResponseDto,
    FormQuestionDto,
    FormSectionDto,
    AttachmentDto,
    SectionInstanceDto,
    SubmittedFormQuestionDto,
    SubmittedFormSectionDto,
    SubmittedFormItemDto,
} from './onsite-visit.dto';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { NotificationSeverity, showSnackbar } from '../notifications/notifications.slice';
import { DomainAdd } from '@mui/icons-material';
import { FormItemRenderer } from './components/FormItemRenderer';
import { QuestionRenderer } from './components/QuestionRenderer';
import { validateFormItems } from './utils/form-validation';

export default function OnsiteVisitSubmitPage() {
    const { id } = useParams();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { selectedBuildings, submissionStatus } = useAppSelector((state) => state.onsiteVisit);

    const [questionEntityMap, setQuestionEntityMap] = useState<Record<string, string[]>>({});
    // Track entity metadata per instance - key format: "section-{identifier}-instance-{index}"
    const [instanceEntityMap, setInstanceEntityMap] = useState<Record<string, string[]>>({});
    const [errors, setErrors] = useState<Record<string, boolean>>({});
    const [responses, setResponses] = useState<Record<string, string>>({});
    const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);
    const uploadedAttachments = useAppSelector((state) => state.onsiteVisit.uploadedAttachments);
    const removedAttachments = useAppSelector((state) => state.onsiteVisit.removedAttachments);

    // Polymorphic form state
    const [polymorphicFormData, setPolymorphicFormData] = useState<FormResponseDto | null>(null);
    const [isLoadingForm, setIsLoadingForm] = useState(true);

    // Create a map of all question/instance IDs to their labels for O(1) lookup
    // This improves performance of error summary banner from O(n*m) to O(n)
    const questionLabelMap = useMemo(() => {
        const labelMap = new Map<string, { label: string; detail?: string }>();

        if (!polymorphicFormData) return labelMap;

        polymorphicFormData.items.forEach((item) => {
            if (item.type === 'question') {
                const question = item as FormQuestionDto;
                labelMap.set(question.id, { label: question.question });
            } else if (item.type === 'section') {
                const section = item as FormSectionDto;
                const sectionKey = `section-${section.sectionIdentifier}`;

                // Add section-level entries
                labelMap.set(sectionKey, {
                    label: section.sectionIdentifier.replace(/_/g, ' '),
                    detail: 'Section error',
                });

                // Add instance-level entries
                section.instances.forEach((instance, instanceIndex) => {
                    const instanceKey = `${sectionKey}-instance-${instanceIndex}`;
                    labelMap.set(instanceKey, {
                        label: `${section.sectionIdentifier.replace(/_/g, ' ')} - Instance ${instanceIndex + 1}`,
                        detail: 'Missing building assignment',
                    });

                    // Add question entries within instances
                    instance.forEach((question) => {
                        labelMap.set(question.id, { label: question.question });
                    });
                });
            }
        });

        return labelMap;
    }, [polymorphicFormData]);

    // Calculate form progress
    const calculateProgress = (): number => {
        if (!polymorphicFormData) return 0;
        const allQuestions: FormQuestionDto[] = [];
        polymorphicFormData.items.forEach((item) => {
            if (item.type === 'question') allQuestions.push(item as FormQuestionDto);
            else if (item.type === 'section') {
                (item as FormSectionDto).instances.forEach((inst) => inst.forEach((q) => allQuestions.push(q)));
            }
        });
        const answered = allQuestions.filter((q) => responses[q.id]?.trim()).length;
        return allQuestions.length > 0 ? Math.round((answered / allQuestions.length) * 100) : 0;
    };

    const handleResponseChange = (questionId: string, value: string | string[]) => {
        setResponses((prev) => ({ ...prev, [questionId]: Array.isArray(value) ? value.join(',') : value }));
        setErrors((prev) => ({ ...prev, [questionId]: false }));
    };

    const handleInstanceEntityChange = (instanceKey: string, entityIds: string[]) => {
        setInstanceEntityMap((prev) => {
            const newMap = {
                ...prev,
                [instanceKey]: entityIds,
            };
            return newMap;
        });
        setErrors((prev) => {
            const newErrors = {
                ...prev,
                [instanceKey]: entityIds.length === 0,
            };
            return newErrors;
        });
    };

    const handleBuildingHighlight = (buildingCode: string | null) => {
        dispatch(setHighlightedBuilding(buildingCode));
    };

    useEffect(() => {
        if (id) {
            setIsLoadingForm(true);
            dispatch(loadPolymorphicForm(id))
                .unwrap()
                .then((data) => {
                    setPolymorphicFormData(data);

                    // Initialize responses from polymorphic form
                    const initialResponses: Record<string, string> = {};
                    data.items.forEach((item) => {
                        if (item.type === 'question') {
                            const question = item as FormQuestionDto;
                            if (question.response) {
                                initialResponses[question.id] = question.response;
                            }
                        } else if (item.type === 'section') {
                            const section = item as FormSectionDto;
                            section.instances.forEach((instance) => {
                                instance.forEach((question) => {
                                    if (question.response) {
                                        initialResponses[question.id] = question.response;
                                    }
                                });
                            });
                        }
                    });
                    setResponses(initialResponses);
                })
                .catch((error) => {
                    console.error('Failed to load form:', error);
                    dispatch(
                        showSnackbar({
                            message: t('Failed to load form'),
                            severity: NotificationSeverity.Error,
                        }),
                    );
                })
                .finally(() => {
                    setIsLoadingForm(false);
                });
        }
        dispatch(setSelectedBuildings([]));
    }, [dispatch, id, t]);

    // Auto-select building for entity-targeted questions when only one building is selected
    useEffect(() => {
        if (selectedBuildings?.length === 1 && polymorphicFormData) {
            const singleBuilding = selectedBuildings[0];

            polymorphicFormData.items.forEach((item) => {
                if (item.type === 'question') {
                    const question = item as FormQuestionDto;
                    if (question.targetEntity === 'Building') {
                        setQuestionEntityMap((prev) => ({
                            ...prev,
                            [question.id]: [singleBuilding.id],
                        }));
                        setErrors((prev) => ({ ...prev, [question.id]: false }));
                    }
                } else if (item.type === 'section') {
                    const section = item as FormSectionDto;
                    const sectionKey = `section-${section.sectionIdentifier}`;

                    if (section.targetEntity === 'Building') {
                        // Auto-select for each instance
                        section.instances.forEach((_, instanceIndex) => {
                            const instanceKey = `${sectionKey}-instance-${instanceIndex}`;
                            setInstanceEntityMap((prev) => ({
                                ...prev,
                                [instanceKey]: [singleBuilding.id],
                            }));
                            setErrors((prev) => ({ ...prev, [instanceKey]: false }));
                        });
                    }
                }
            });
        } else {
            // Clear selections or keep only valid ones
            const validBuildingIds = new Set(selectedBuildings?.map((b) => b.id) || []);

            setQuestionEntityMap((prev) => {
                const newMap: Record<string, string[]> = {};
                Object.entries(prev).forEach(([key, ids]) => {
                    // Keep only IDs that are in current selected buildings
                    const validIds = ids.filter((id) => validBuildingIds.has(id));
                    if (validIds.length > 0) {
                        newMap[key] = validIds;
                    }
                });
                return newMap;
            });

            setInstanceEntityMap((prev) => {
                const newMap: Record<string, string[]> = {};
                Object.entries(prev).forEach(([key, ids]) => {
                    // Keep only IDs that are in current selected buildings
                    const validIds = ids.filter((id) => validBuildingIds.has(id));
                    if (validIds.length > 0) {
                        newMap[key] = validIds;
                    }
                });
                return newMap;
            });
        }
    }, [selectedBuildings, polymorphicFormData]);

    const handleBuildingSelect = useCallback(
        async (selected: Building[]) => {
            // Simply update the selected buildings state
            dispatch(setSelectedBuildings(selected));
        },
        [dispatch],
    );

    // Helper: Build question attachments
    const buildQuestionAttachments = (questionId: string, questionAttachments: AttachmentDto[]): AttachmentDto[] => {
        const uploaded = uploadedAttachments[questionId] || [];
        const initial = questionAttachments || [];
        const removed = removedAttachments[questionId] || [];
        return [...initial, ...uploaded].filter((att) => !removed.some((rid) => rid === att.externalId));
    };

    // Helper: Build submitted question DTO
    const buildSubmittedQuestion = (
        question: FormQuestionDto,
        targetEntity: string | null = null,
        targetId: string | null = null,
    ): SubmittedFormQuestionDto => {
        const attachments = buildQuestionAttachments(question.id, question.attachments);
        return {
            type: 'question' as const,
            questionId: question.id,
            questionTag: question.tag,
            targetEntity: targetEntity,
            targetId: targetId,
            response: responses[question.id] || question.response || '',
            attachments: attachments,
            buildingDataProductId: targetId ? getBuildingDataProductId(targetId, targetEntity) : null,
        };
    };

    // Helper: Build standalone question submissions (handles multi-entity)
    const buildStandaloneQuestionSubmissions = (question: FormQuestionDto): SubmittedFormQuestionDto[] => {
        const entities = questionEntityMap[question.id] || [];
        const isEntityTarget = question.targetEntity === 'Building';
        const entityIds = isEntityTarget ? (entities.length > 0 ? entities : [null]) : [null];

        // For entity-targeted questions, create one submission per entity
        if (entityIds.length > 1 || (entityIds.length === 1 && entityIds[0] !== null)) {
            return entityIds.map((entityId) => buildSubmittedQuestion(question, question.targetEntity, entityId));
        }

        return [buildSubmittedQuestion(question, question.targetEntity, null)];
    };

    // Helper: Build section instance
    const buildSectionInstance = (
        instance: FormQuestionDto[],
        targetEntity: string | null,
        targetId: string | null,
    ): SectionInstanceDto => {
        const questions: SubmittedFormQuestionDto[] = instance.map((question) =>
            buildSubmittedQuestion(question, null, null),
        );

        return {
            targetEntity: targetEntity,
            targetId: targetId,
            buildingDataProductId: targetId ? getBuildingDataProductId(targetId, targetEntity) : null,
            questions: questions,
        };
    };

    // Helper: Build section submissions (handles multi-entity per instance)
    const buildSectionSubmission = (section: FormSectionDto): SubmittedFormSectionDto => {
        const sectionKey = `section-${section.sectionIdentifier}`;

        // Build instances with metadata at instance level
        // For each instance, if multiple buildings selected, create one instance per building
        const instances: SectionInstanceDto[] = section.instances.flatMap((instance, instanceIndex) => {
            const instanceKey = `${sectionKey}-instance-${instanceIndex}`;
            const instanceEntities = instanceEntityMap[instanceKey] || [];

            // If no entities selected, create one instance with null
            if (instanceEntities.length === 0) {
                return [buildSectionInstance(instance, section.targetEntity, null)];
            }

            // For each selected entity, create an instance
            return instanceEntities.map((entityId) => buildSectionInstance(instance, section.targetEntity, entityId));
        });

        return {
            type: 'section' as const,
            sectionIdentifier: section.sectionIdentifier,
            instances: instances,
        };
    };

    const handleSubmit = async () => {
        if (!id || !polymorphicFormData) return;

        // Validate all items using extracted helper
        const { errors: newErrors, hasErrors } = validateFormItems(
            polymorphicFormData.items,
            responses,
            questionEntityMap,
            instanceEntityMap,
        );

        setErrors(newErrors);
        if (hasErrors) {
            setFormErrorMessage(t('Please fix the validation errors before submitting.'));
            return;
        }

        try {
            // Build submission payload
            const submitItems: SubmittedFormItemDto[] = polymorphicFormData.items.flatMap((item) => {
                if (item.type === 'question') {
                    return buildStandaloneQuestionSubmissions(item as FormQuestionDto) as SubmittedFormItemDto[];
                }
                return [buildSectionSubmission(item as FormSectionDto)] as SubmittedFormItemDto[];
            });

            await dispatch(submitPolymorphicForm({ visitId: id, payload: { items: submitItems } })).unwrap();

            dispatch(
                showSnackbar({
                    message: t('Form submitted successfully'),
                    severity: NotificationSeverity.Success,
                }),
            );
        } catch (error) {
            console.error('Failed to submit polymorphic form:', error);
            dispatch(
                showSnackbar({
                    message: t('Failed to submit form'),
                    severity: NotificationSeverity.Error,
                }),
            );
        }
    };

    const getBuildingDataProductId = (entityId: string | null, targetEntity: string | null): string | null => {
        if (!selectedBuildings || !entityId || !targetEntity) {
            return null;
        }

        // Helper function to sanitize dataProductId (convert empty strings to null)
        const sanitizeDataProductId = (value: string | undefined | null): string | null => {
            if (!value || value.trim() === '') {
                return null;
            }
            return value;
        };

        // Handle different entity types
        switch (targetEntity) {
            case 'Building': {
                const building = selectedBuildings.find((b) => b.id === entityId);
                return sanitizeDataProductId(building?.dataProductId);
            }

            // Add more entity types here as they are introduced

            default:
                // Log warning for unsupported entity types to aid debugging
                console.warn(`Unsupported targetEntity type: ${targetEntity}`);
                return null;
        }
    };

    const formatSelectedIds = (selected: string[], selectedBuildings: Building[] | null): string => {
        return selected
            .map((id) => {
                const buildingMatch = selectedBuildings?.find((b) => b.id === id);

                if (buildingMatch?.code) return buildingMatch.code;

                return id;
            })
            .join(', ');
    };

    useEffect(() => {
        // Show submission status messages
        if (submissionStatus?.message) {
            dispatch(
                showSnackbar({
                    message: submissionStatus.message,
                    severity:
                        submissionStatus.type === 'success' ? NotificationSeverity.Success : NotificationSeverity.Error,
                }),
            );
        }

        // Show form validation error
        if (formErrorMessage) {
            dispatch(
                showSnackbar({
                    message: formErrorMessage,
                    severity: NotificationSeverity.Error,
                }),
            );
            setFormErrorMessage(null);
        }
    }, [submissionStatus, formErrorMessage, dispatch]);

    if (!id) {
        return <Typography>{t('No Onsite Visit ID Provided')}</Typography>;
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'grey.50' }}>
                {/* Header with Progress */}
                <Paper
                    elevation={0}
                    sx={{
                        background: (theme) =>
                            `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        color: 'white',
                        p: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                🏗️ {t('Onsite Visit')}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {polymorphicFormData && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        {t('Form Progress')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {calculateProgress()}%
                                        </Typography>
                                        <LinearProgress
                                            variant="determinate"
                                            value={calculateProgress()}
                                            sx={{
                                                width: 80,
                                                height: 6,
                                                borderRadius: 3,
                                                bgcolor: 'rgba(255, 255, 255, 0.3)',
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 3,
                                                    bgcolor: 'white',
                                                },
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                        {t(
                            'Complete the onsite visit form by answering all required questions and selecting buildings',
                        )}
                    </Typography>
                </Paper>

                <Box sx={{ p: 2, flex: 1, display: 'flex', gap: 2, overflow: 'hidden' }}>
                    {/* Left Panel: Map & Building Selection */}
                    <Paper
                        elevation={2}
                        sx={{
                            width: '400px',
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: 3,
                            overflow: 'hidden',
                            flexShrink: 0,
                        }}
                    >
                        {/* Building Search & Import */}
                        <Box
                            sx={{
                                p: 2,
                                bgcolor: 'background.default',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                📍 {t('Select Buildings')}
                            </Typography>
                            <Stack spacing={1.5}>
                                <Search />
                                <Link to="/pv-analysis/import-building" style={{ textDecoration: 'none' }}>
                                    <Button
                                        fullWidth
                                        startIcon={<DomainAdd />}
                                        variant="outlined"
                                        size="small"
                                        sx={{ borderRadius: 2 }}
                                    >
                                        {t('Import Building')}
                                    </Button>
                                </Link>
                            </Stack>
                        </Box>

                        {/* Map */}
                        <Box
                            sx={{
                                flex: 1,
                                position: 'relative',
                                minHeight: 0,
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '3px',
                                    background: (theme) =>
                                        `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                    zIndex: 1,
                                },
                            }}
                        >
                            <OnsiteVisitMap
                                onSelectBuildings={(buildings) => handleBuildingSelect(buildings)}
                                selectedBuildings={selectedBuildings}
                            />
                        </Box>

                        {/* Selected Buildings Summary */}
                        {selectedBuildings && selectedBuildings.length > 0 && (
                            <Box sx={{ p: 2, bgcolor: 'primary.50', borderTop: '1px solid', borderColor: 'divider' }}>
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}
                                >
                                    {t('Selected Buildings')}
                                </Typography>
                                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                                    {selectedBuildings.map((building) => (
                                        <Chip
                                            key={building.id}
                                            label={building.code}
                                            size="small"
                                            color="primary"
                                            variant="filled"
                                            sx={{ fontWeight: 600 }}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Paper>

                    {/* Right Panel: Form Content */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
                        <Paper
                            elevation={2}
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: 3,
                                overflow: 'hidden',
                            }}
                        >
                            {/* Form Header */}
                            <Box
                                sx={{
                                    p: 2,
                                    bgcolor: 'background.default',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
                                >
                                    📋 {t('Onsite Visit Questions')}
                                </Typography>
                            </Box>

                            {/* Form Content - Scrollable */}
                            <Box
                                sx={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    p: 3,
                                    bgcolor: 'grey.50',
                                }}
                            >
                                {isLoadingForm ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            py: 8,
                                        }}
                                    >
                                        <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
                                        <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                            {t('Loading onsite visit form...')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.disabled', mt: 1 }}>
                                            {t('Please wait while we prepare your form')}
                                        </Typography>
                                    </Box>
                                ) : polymorphicFormData ? (
                                    <Stack spacing={2}>
                                        {/* Error Summary Banner */}
                                        {Object.keys(errors).some((key) => errors[key]) && (
                                            <Alert
                                                severity="error"
                                                icon={<ErrorOutlineIcon sx={{ fontSize: 28 }} />}
                                                sx={{
                                                    mb: 3,
                                                    borderRadius: 2,
                                                    border: '2px solid',
                                                    borderColor: 'error.light',
                                                    boxShadow: '0 4px 12px rgba(211, 47, 47, 0.15)',
                                                }}
                                            >
                                                <AlertTitle sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                                                    ⚠️ {t('Form has errors that need to be fixed')}
                                                </AlertTitle>
                                                <Divider sx={{ my: 1.5, borderColor: 'error.light' }} />
                                                <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                                                    {t('Please review and correct the following issues:')}
                                                </Typography>
                                                <List dense sx={{ bgcolor: 'error.lighter', borderRadius: 1 }}>
                                                    {Object.entries(errors)
                                                        .filter(([_, hasError]) => hasError)
                                                        .slice(0, 5)
                                                        .map(([key, _]) => {
                                                            // Use O(1) map lookup instead of nested loops
                                                            const labelInfo = questionLabelMap.get(key);
                                                            const errorLabel = labelInfo?.label || key;
                                                            const errorDetail = labelInfo?.detail || '';

                                                            return (
                                                                <ListItem key={key} disablePadding>
                                                                    <ListItemButton
                                                                        dense
                                                                        onClick={() => {
                                                                            const element =
                                                                                document.getElementById(key);
                                                                            element?.scrollIntoView({
                                                                                behavior: 'smooth',
                                                                                block: 'center',
                                                                            });
                                                                        }}
                                                                    >
                                                                        <ListItemText
                                                                            primary={errorLabel}
                                                                            secondary={errorDetail}
                                                                            slotProps={{
                                                                                primary: {
                                                                                    variant: 'body2',
                                                                                    sx: { fontWeight: 500 },
                                                                                },
                                                                                secondary: {
                                                                                    variant: 'caption',
                                                                                    color: 'error',
                                                                                },
                                                                            }}
                                                                        />
                                                                    </ListItemButton>
                                                                </ListItem>
                                                            );
                                                        })}
                                                </List>
                                                {Object.keys(errors).filter((key) => errors[key]).length > 5 && (
                                                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                                                        {t(
                                                            `...and ${Object.keys(errors).filter((key) => errors[key]).length - 5} more error(s)`,
                                                        )}
                                                    </Typography>
                                                )}
                                            </Alert>
                                        )}

                                        {polymorphicFormData.items.map((item, index) => {
                                            if (item.type === 'question') {
                                                const question = item as FormQuestionDto;
                                                return (
                                                    <Fade in timeout={300 + index * 50} key={question.id}>
                                                        <div>
                                                            <Card
                                                                variant="outlined"
                                                                id={question.id}
                                                                sx={{
                                                                    p: 3,
                                                                    borderRadius: 3,
                                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                    border: errors[question.id]
                                                                        ? '2px solid'
                                                                        : '1px solid',
                                                                    borderColor: errors[question.id]
                                                                        ? 'error.main'
                                                                        : 'divider',
                                                                    bgcolor: 'background.paper',
                                                                    '&:hover': {
                                                                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                                                        transform: 'translateY(-2px)',
                                                                        borderColor: 'primary.main',
                                                                    },
                                                                }}
                                                            >
                                                                <QuestionRenderer
                                                                    question={question}
                                                                    value={
                                                                        responses[question.id] ||
                                                                        question.response ||
                                                                        ''
                                                                    }
                                                                    onChange={(value) =>
                                                                        handleResponseChange(question.id, value)
                                                                    }
                                                                    error={errors[question.id]}
                                                                    helperText={
                                                                        errors[question.id]
                                                                            ? t('This field is required')
                                                                            : ''
                                                                    }
                                                                    t={t}
                                                                />

                                                                {/* Building Selection */}
                                                                {question.targetEntity === 'Building' && (
                                                                    <FormControl
                                                                        fullWidth
                                                                        sx={{ mt: 2 }}
                                                                        error={errors[question.id]}
                                                                    >
                                                                        <InputLabel
                                                                            id={`building-select-label-${question.id}`}
                                                                        >
                                                                            {t('Select Building')}
                                                                        </InputLabel>
                                                                        <Select
                                                                            labelId={`building-select-label-${question.id}`}
                                                                            label={t('Select Building')}
                                                                            value={
                                                                                questionEntityMap[question.id]?.[0] ||
                                                                                ''
                                                                            }
                                                                            disabled={
                                                                                !selectedBuildings ||
                                                                                selectedBuildings.length === 0
                                                                            }
                                                                            onChange={(e) => {
                                                                                const value = e.target.value as string;
                                                                                if (value) {
                                                                                    setQuestionEntityMap((prev) => ({
                                                                                        ...prev,
                                                                                        [question.id]: [value],
                                                                                    }));
                                                                                    setErrors((prev) => ({
                                                                                        ...prev,
                                                                                        [question.id]: false,
                                                                                    }));
                                                                                }
                                                                            }}
                                                                            onOpen={() => handleBuildingHighlight(null)}
                                                                            onClose={() =>
                                                                                handleBuildingHighlight(null)
                                                                            }
                                                                            onMouseEnter={() =>
                                                                                handleBuildingHighlight(
                                                                                    questionEntityMap[
                                                                                        question.id
                                                                                    ]?.[0] || null,
                                                                                )
                                                                            }
                                                                            onMouseLeave={() =>
                                                                                handleBuildingHighlight(null)
                                                                            }
                                                                        >
                                                                            {selectedBuildings &&
                                                                            selectedBuildings.length > 0 ? (
                                                                                selectedBuildings.map((building) => (
                                                                                    <MenuItem
                                                                                        key={building.id}
                                                                                        value={building.id}
                                                                                    >
                                                                                        {building.code}
                                                                                    </MenuItem>
                                                                                ))
                                                                            ) : (
                                                                                <MenuItem disabled>
                                                                                    {t('Please, select a building')}
                                                                                </MenuItem>
                                                                            )}
                                                                        </Select>
                                                                        {errors[question.id] && (
                                                                            <Typography
                                                                                color="error"
                                                                                variant="caption"
                                                                                sx={{ mt: 0.5 }}
                                                                            >
                                                                                {t('This selection is required')}
                                                                            </Typography>
                                                                        )}
                                                                        {!selectedBuildings ||
                                                                        selectedBuildings.length === 0 ? (
                                                                            <Typography
                                                                                variant="caption"
                                                                                sx={{
                                                                                    color: 'text.secondary',
                                                                                    mt: 0.5,
                                                                                }}
                                                                            >
                                                                                {t(
                                                                                    'Please select buildings from the map first',
                                                                                )}
                                                                            </Typography>
                                                                        ) : null}
                                                                    </FormControl>
                                                                )}
                                                            </Card>
                                                        </div>
                                                    </Fade>
                                                );
                                            }

                                            // Section rendering
                                            return (
                                                <FormItemRenderer
                                                    key={`section-${(item as FormSectionDto).sectionIdentifier}-${index}`}
                                                    item={item as FormSectionDto}
                                                    responses={responses}
                                                    errors={errors}
                                                    onResponseChange={handleResponseChange}
                                                    selectedBuildings={selectedBuildings}
                                                    instanceEntityMap={instanceEntityMap}
                                                    onInstanceEntityChange={handleInstanceEntityChange}
                                                    formatSelectedIds={formatSelectedIds}
                                                    t={t}
                                                />
                                            );
                                        })}
                                    </Stack>
                                ) : (
                                    <Typography color="error">{t('Failed to load form')}</Typography>
                                )}
                            </Box>
                        </Paper>
                    </Box>
                </Box>

                {/* Floating Submit Button */}
                <Fab
                    color="primary"
                    aria-label="submit"
                    onClick={handleSubmit}
                    disabled={submissionStatus?.isLoading}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        '&:hover': {
                            transform: 'translateY(-2px)',
                        },
                    }}
                >
                    {submissionStatus?.isLoading ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
                </Fab>
            </Box>
        </LocalizationProvider>
    );
}
